import { createRouter, createWebHistory } from "vue-router";
import CryptoJS from "crypto-js";

// Components
import dashboardView from "@/views/dashboard_one/dashboardView.vue";
import landingPage from "@/views/landing_view/landing_page.vue";
import loginView from "@/components/Login/LoginView.vue";
import ProfileSection from "@/components/Profile/ProfileSection.vue";
import EditProfile from "@/components/EditProfile/EditProfile.vue";
import SettingsSection from "@/components/Settings/SettingsSection.vue";
import UserManagementSection from "@/components/UserManagement/UserManagementSection.vue";
import ResetPassword from "@/components/ResetPassword/ResetPassword.vue";
import PasswordResetConfirm from "@/components/PasswordResetConfirm/PasswordResetConfirm.vue";
import PasswordResetComplete from "@/components/PasswordResetComplete/PasswordResetComplete.vue";
import OneTimePin from "@/components/OneTimePin/OneTimePin.vue";
import SsoAssertView from "@/components/SsoAssert/SsoAssertView.vue";
import is_admin_user from "@/utils/auth_user";
import {
  fetch_user_property_assignments,
  get_assignment_properties,
  get_assignment_property_ids
} from "@/utils/user_property_assignments";

const sso_assert_path = process.env.VUE_APP_SSO_ASSERT_PATH || "/sso/assert";

const routes = [
  {
    path: "/home",
    name: "dashboard_view",
    component: dashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: "/",
    redirect: "/login",
  },
  {
    path: "/landing",
    name: "landing_page",
    component: landingPage,
  },
  {
    path: "/login",
    name: "login",
    component: loginView,
  },
  {
    path: "/one_time_pin",
    name: "one_time_pin",
    component: OneTimePin,
  },
  {
    path: sso_assert_path,
    name: "sso_assert",
    component: SsoAssertView,
    meta: { requiresAuth: false, otpRequired: false }
  },
  {
    path: "/reset_password",
    name: "reset_password",
    component: ResetPassword,
  },
  {
    path: "/reset-password",
    alias: "/password_reset_confirm",
    name: "password_reset_confirm",
    component: PasswordResetConfirm,
  },
  {
    path: "/password_reset_complete",
    name: "password_reset_complete",
    component: PasswordResetComplete,
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileSection,
    meta: { requiresAuth: true }
  },
  {
    path: "/edit_profile",
    name: "edit_profile",
    component: EditProfile,
    meta: { requiresAuth: true }
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsSection,
    meta: { requiresAuth: true }
  },
  {
    path: "/user_management",
    name: "user_management",
    component: UserManagementSection,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
];

// Get encryption key from environment or use default fallback
const encryption_key = process.env.VUE_APP_ENCRYPTION_KEY || "default-secret-key-change-in-production";
const get_profile_url = process.env.VUE_APP_VIEW_USER_URL;

export function set_token(token) {
    if (!token) {
      throw new Error('Token is required');
    }
    var encrypted_token = CryptoJS.AES.encrypt(token, encryption_key).toString();
    sessionStorage.setItem("encrypted_token", encrypted_token);
}

export function get_token() {
  try {
    var encrypted_token = sessionStorage.getItem("encrypted_token");

    if (!encrypted_token) {
      return null;
    }

    var decrypted_token = CryptoJS.AES.decrypt(
      encrypted_token,
      encryption_key
    );

    var token = decrypted_token.toString(CryptoJS.enc.Utf8);
    
    if (!token) {
      throw new Error('Failed to decrypt token - possibly wrong encryption key');
    }

    return token;
  } catch (error) {
    return null;
  }
}

export function set_user_details(user_details) {
  try {
    var role = user_details.role_type || user_details.role || null;
    var user_id = user_details.user_id || null;
    var last_name = user_details.last_name || null;
    var first_name = user_details.first_name || null;

    sessionStorage.setItem("role", role);
    sessionStorage.setItem("user_id", user_id);
    sessionStorage.setItem("last_name", last_name);
    sessionStorage.setItem("first_name", first_name);
    
  } catch (error) {
    console.error('Error setting user details:', error);
  }
}

export function get_user_details() {
  var role = sessionStorage.getItem("role");
  var user_id = sessionStorage.getItem("user_id");
  var last_name = sessionStorage.getItem("last_name");
  var first_name = sessionStorage.getItem("first_name");

  var user_details = {
    role: role,
    user_id: user_id,
    last_name: last_name,
    first_name: first_name,
  };
  return user_details;
}

// NEW: Function to set complete user profile in sessionStorage
export function set_user_profile(user_profile) {
  try {
    const encrypted_profile = CryptoJS.AES.encrypt(
      JSON.stringify(user_profile), 
      encryption_key
    ).toString();
    sessionStorage.setItem("encrypted_user_profile", encrypted_profile);
  } catch (error) {
    console.error('Error setting user profile:', error);
  }
}

function get_query_value(value) {
  return Array.isArray(value) ? value[0] : value;
}

export async function complete_sso_login(token, raw_payload) {
  const clean_token = get_query_value(token);
  const clean_payload = get_query_value(raw_payload);

  if (!clean_token || !clean_payload) {
    throw new Error("SSO token and payload are required.");
  }

  const payload = typeof clean_payload === "string"
    ? JSON.parse(clean_payload)
    : clean_payload;

  if (!payload.user_id && payload.id) {
    payload.user_id = payload.id;
  }

  if (!payload.user_id) {
    throw new Error("SSO payload did not include a user ID.");
  }

  set_token(clean_token);
  set_user_details(payload);
  set_user_profile(payload);

  sessionStorage.setItem("is_sso_login", "true");
  sessionStorage.setItem("sso_user_payload", JSON.stringify(payload));

  await fetch_and_store_user_profile();

  return payload;
}

// NEW: Function to get complete user profile from sessionStorage
export function get_user_profile() {
  try {
    const encrypted_profile = sessionStorage.getItem("encrypted_user_profile");
    
    if (!encrypted_profile) {
      return null;
    }

    const decrypted_profile = CryptoJS.AES.decrypt(
      encrypted_profile,
      encryption_key
    );

    const profile_string = decrypted_profile.toString(CryptoJS.enc.Utf8);
    
    if (!profile_string) {
      return null;
    }

    return JSON.parse(profile_string);
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

// NEW: Function to fetch and store user profile globally
export async function fetch_and_store_user_profile() {
  try {
    const user_details = get_user_details();
    const user_id = user_details.user_id;
    const auth_token = get_token();

    if (!user_id || !auth_token || !get_profile_url) {
      return null;
    }

    const base_profile_url = get_profile_url.endsWith("/")
      ? get_profile_url
      : `${get_profile_url}/`;
    const profile_url = `${base_profile_url}${user_id}/`;

    const response = await fetch(profile_url, {
      method: 'GET',
      headers: {
        'Authorization': `Token ${auth_token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    let user_profile = safe_extract_user_data(data);
    user_profile = await enrich_user_profile_with_property_assignments(user_profile, auth_token);
    
    // Store the complete profile in sessionStorage
    set_user_profile(user_profile);
    
    return user_profile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

async function enrich_user_profile_with_property_assignments(user_profile, auth_token) {
  const user_id = user_profile?.id || user_profile?.user_id;

  if (!user_id || !auth_token) {
    return user_profile;
  }

  try {
    const assignments = await fetch_user_property_assignments(user_id, {
      Authorization: `Token ${auth_token}`,
      Accept: "application/json"
    }, {
      include_inactive: true
    });
    const active_assignments = assignments.filter((assignment) => assignment.is_active);

    return ({
      ...user_profile,
      property_assignments: assignments,
      property_ids: get_assignment_property_ids(active_assignments),
      assigned_properties: get_assignment_properties(active_assignments)
    });
  } catch (error) {
    console.error("Error fetching user property assignments:", error);
    return user_profile;
  }
}

// NEW: Safe extraction function for router
function safe_extract_user_data(apiResponse) {
  if (typeof apiResponse === 'string') {
    try {
      apiResponse = JSON.parse(apiResponse);
    } catch (e) {
      throw new Error('Invalid JSON response');
    }
  }
  
  let status;

  if (apiResponse.status !== undefined) {
    status = apiResponse.status;
  } else if (apiResponse['status'] !== undefined) {
    status = apiResponse['status'];
  }

  if (status && status !== 'success') {
    throw new Error(`API returned non-success status: ${status}`);
  }

  if (!status) {
    const direct_user = apiResponse.user || apiResponse.User || apiResponse;
    if (direct_user && (direct_user.id || direct_user.username || direct_user.email)) {
      return ({
        ...direct_user,
        phone_number: direct_user.phone_number || direct_user.phoneNumber,
        is_first_login: direct_user.is_first_login !== undefined ? direct_user.is_first_login : direct_user.isFirstLogin,
        initial_complete_profile: direct_user.initial_complete_profile !== undefined ? direct_user.initial_complete_profile : direct_user.initialCompleteProfile
      });
    }
  }

  const data = apiResponse.data || apiResponse.Data || [];

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('No user data found in response');
  }

  const first_item = data[0];
  const user_object = first_item.user || first_item.User || first_item;

  if (!user_object) {
    throw new Error('User data not found in response');
  }

  const user_profile = {
    ...user_object,
    phone_number: first_item.phone_number || first_item.phoneNumber || user_object.phone_number,
    is_first_login: first_item.is_first_login !== undefined ? first_item.is_first_login : first_item.isFirstLogin,
    initial_complete_profile: first_item.initial_complete_profile !== undefined ? first_item.initial_complete_profile : first_item.initialCompleteProfile
  };

  return user_profile;
}

export function is_authenticated() {
  const token = sessionStorage.getItem("encrypted_token");
  return token !== null && token !== undefined && token !== '';
}

export function clear_token() {
  sessionStorage.removeItem("encrypted_token");
  sessionStorage.removeItem("encrypted_user_profile");
  sessionStorage.removeItem("role");
  sessionStorage.removeItem("user_id");
  sessionStorage.removeItem("last_name");
  sessionStorage.removeItem("first_name");
  sessionStorage.removeItem("is_sso_login");
  sessionStorage.removeItem("sso_user_payload");
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// NEW: Enhanced navigation guard to fetch profile on auth routes
router.beforeEach(async (to, from, next) => {
  if (to.query.token && to.query.payload) {
    try {
      await complete_sso_login(to.query.token, to.query.payload);
      next({ path: "/home", replace: true });
    } catch (error) {
      console.error("Could not complete SSO login:", error);
      clear_token();
      next({ path: "/login", replace: true });
    }
    return;
  }

  const isAuthenticated = is_authenticated();

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (isAuthenticated) {
      // Fetch and store user profile if not already available
      let existing_profile = get_user_profile();
      if (!existing_profile) {
        existing_profile = await fetch_and_store_user_profile();
      }

      if (to.matched.some((record) => record.meta.requiresAdmin) && !is_admin_user(existing_profile)) {
        next("/home");
        return;
      }

      next();
    } else {
      next("/login");
    }
  } else {
    next();
  }
});

export default router;
